import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import toNumber from 'lodash/toNumber';

import type { LooseObject } from './types';

export const removeTypenameFromObject = <T extends { __typename: string }>(initialObject: T): Omit<T, '__typename'> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, ...rest } = initialObject;
  return rest as Omit<T, '__typename'>;
};

export const removeTypenameDeep = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => removeTypenameDeep(item)) as T;
  }
  if (!isNil(value) && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).reduce<Record<string, unknown>>((accumulator, key) => {
      if (key !== '__typename') {
        accumulator[key] = removeTypenameDeep((value as Record<string, unknown>)[key]);
      }
      return accumulator;
    }, {}) as T;
  }
  return value;
};

export const isNilOrEmpty = (data: unknown): data is null | undefined | '' => isNil(data) || isEmpty(data);

export const parseJsonOrUndefined = (value: string): LooseObject | undefined => {
  try {
    return JSON.parse(value) as LooseObject;
  } catch {
    return undefined;
  }
};

export const getObjectValue = <T>(obj: T, path: string): unknown => {
  const segments = path.split('.');
  const [key, index] = parseSegment(segments[0]);
  const value = obj && obj.hasOwnProperty(key) ? obj[key as keyof T] : undefined;

  if (segments.length === 1) {
    return index && Array.isArray(value) ? findArrayItemValue(value, index) : value;
  }

  if (index && Array.isArray(value)) {
    return findArrayItemValue(value, index, segments.slice(1).join('.'));
  }

  return value ? getObjectValue(value, segments.slice(1).join('.')) : undefined;
};

const parseSegment = (segment: string): [string, string?] => {
  const matches = segment.match(/^(\w+)(?:\[(.+?)\])?$/);

  if (!matches) {
    throw new Error(`Invalid path segment!`);
  }
  return [matches[1], matches[2]];
};

const findArrayItemValue = <T extends Record<string, unknown>, K extends keyof T>(
  arr: T[],
  index: string,
  key: K = 'value' as K
): T[K] | undefined => {
  const match = index.match(/(.+?)\s*===\s*"(.+?)"/);

  let item;
  if (Array.isArray(arr) && match) {
    const [matcherKey, matcherValue] = match.slice(1);
    item = arr.find((foundItem) => foundItem[matcherKey] === matcherValue);
  }
  return item ? item[key] : item;
};

/**
 * Get safe value of a field in an object. The field can contains . to go in sub object.
 * @param {*} object The object
 * @param {*} field The field name we want to have value
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSafeValueInObject = <T>(object?: T | null, field?: keyof T | string | null): any => {
  if (isNil(object) || isEmpty(object) || isNil(field) || isEmpty(field)) return '';
  const fieldString = field as string;
  if (fieldString.indexOf('.') === -1) {
    if (fieldString.endsWith(']')) {
      const tableIndex = fieldString.indexOf('[');
      const fieldName = fieldString.substring(0, tableIndex);
      const indexToSearch = fieldString.substring(tableIndex + 1, fieldString.length - 1);
      if (isNil(object[fieldName as keyof T])) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (object as any)[fieldName][toNumber(indexToSearch)];
    }
    return (isNil(object[field as keyof T]) ? '' : object[field as keyof T]) as string;
  }
  const fields = fieldString.split('.');
  if (fields[0].endsWith(']')) {
    const tableIndex = fields[0].indexOf('[');
    const baseField = fields[0].substring(0, tableIndex);
    const indexToSearch = fields[0].substring(tableIndex + 1, fields[0].length - 1);
    if (isNil(object[baseField as keyof T])) return '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return getSafeValueInObject((object as any)[baseField][toNumber(indexToSearch)], fields.slice(1).join('.'));
  }
  return getSafeValueInObject(object[fields[0] as keyof T], fields.slice(1).join('.'));
};

/**
 * Get safe value of a field in an array containing the object.
 * @param {*} array The array
 * @param {Int} id The id of object inside the array
 * @param {*} field The field name we want to have value
 */
export const getSafeValueInArray = <T extends { id: string }>(
  array?: T[] | null,
  id?: string,
  field?: string | null
) => {
  if (isNil(array) || isEmpty(array) || isNil(field) || isEmpty(field)) return '';

  const currentObject = array.find((o) => o.id === id);
  if (isNil(currentObject)) {
    return '';
  }
  return getSafeValueInObject(currentObject, field);
};

export const getErrorMessage = (error: Error | null | undefined): string | null =>
  isNil(error) ? null : error.message;
